import {Component, computed, inject, resource} from '@angular/core';
import {environment} from "../../../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import typia from "typia";
import {Skeleton} from "primeng/skeleton";
import {ApartmentService} from "../../../services/apartment.service";
import {HomeyImageDirective} from "../../../directives/homey-image.directive";
import {ApartmentDto, HomeyUserEventPublicDto} from "homey-housing-lottery-app-dto";
import {CarouselModule} from "primeng/carousel";
import {boroughToText, BoroughToTextPipe} from "../../../pipes/borough-to-text.pipe";
import {unitTypeToTextSingular} from "lottery-shared";
import {ABService} from "../../../services/a-b.service";

type DisplayUserEvent = HomeyUserEventPublicDto & {
  lottery?: ApartmentDto;
  photo?: string;
  location: string;
  displayName: string;
  boroughLabel: string;
  unitLabel: string;
  includeLocation: boolean;
  emoji: string;
};

@Component({
  selector: 'homey-user-events-feed',
  standalone: true,
  imports: [
    HomeyImageDirective,
    CarouselModule,
    BoroughToTextPipe,
    Skeleton
  ],
  templateUrl: './user-events-feed.component.html',
  styleUrl: './user-events-feed.component.scss'
})
export class UserEventsFeedComponent {
  private readonly http = inject(HttpClient);
  private readonly apartmentService = inject(ApartmentService);
  private readonly abService = inject(ABService);
  protected readonly apartments = this.apartmentService.apartments;
  private readonly emojiSequence = ['🏡', '❤️', '🚀', '🎉', '🗽', '🏠', '🧡', '🔥', '🎊'];
  protected readonly skeletonPlaceholders = [1, 2, 3];
  protected readonly eventFeed = this.abService.eventFeed;

  protected readonly recentUserEvents = resource<HomeyUserEventPublicDto[], null>({
    loader: async () => {
      try {
        const response = await lastValueFrom(this.http.get<unknown>(`${environment.apiUrl}/api/public/user-events`));

        if (!Array.isArray(response)) {
          return [];
        }

        return response.filter((event): event is HomeyUserEventPublicDto => typia.is<HomeyUserEventPublicDto>(event));
      } catch (e) {
        console.error("Failed to load user events", e);
        return [];
      }
    }
  });

  protected readonly displayedUserEvents = computed<DisplayUserEvent[] | undefined>(() => {
    const events = this.recentUserEvents.value();
    const apartments = this.apartments();

    if (!events) {
      return undefined;
    }

    if(!apartments){
      return undefined;
    }

    return events.map((event, index) => {
      const emoji = this.emojiSequence[index % this.emojiSequence.length];
      const apartment = apartments?.find(
        apt => apt.id === event.lotteryId || apt.slug === event.lotteryId
      );

      if(!apartment){
        return undefined
      }

      const photo = apartment?.mainPhoto?.baseImage ?? apartment?.photos?.[0]?.baseImage;
      const boroughLabel = boroughToText(event.borough);
      const location = apartment?.neighborhood;
      const unitLabel = unitTypeToTextSingular(event.largestEligibleUnitType);
      const includeLocation = this.shouldIncludeLocation(unitLabel, apartment?.name, location);

      return {
        ...event,
        displayName: this.buildDisplayName(event.firstNameInitial, event.lastNameInitial),
        boroughLabel,
        lottery: apartment,
        photo,
        location,
        unitLabel,
        includeLocation,
        emoji
      };
    }).filter((event) => event !== undefined).slice(0, 3 * 6)
  });

  protected formatRelativeTime(createdAt: string): string {
    const created = new Date(createdAt);
    const diffMs = Date.now() - created.getTime();

    if (Number.isNaN(diffMs)) {
      return '';
    }

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diffMs < hour) {
      const minutes = Math.max(1, Math.floor(diffMs / minute));
      return `${minutes}m`;
    }

    if (diffMs < day) {
      const hours = Math.floor(diffMs / hour);
      return `${hours}h`;
    }

    const days = Math.floor(diffMs / day);
    return `${days}d`;
  }

  private buildDisplayName(firstInitial: string, lastInitial: string): string {
    const initials = [firstInitial, lastInitial].filter(Boolean).map(initial => `${initial}.`);
    return initials.join(' ').trim();
  }

  private shouldIncludeLocation(unitLabel: string, lotteryName?: string, location?: string): boolean {
    if (!location) {
      return false;
    }

    const baseName = lotteryName ?? "this listing";
    const baseDescription = `Was selected for a ${unitLabel} at ${baseName}`;

    return (baseDescription.length + 2 + location.length) <= 65;
  }
}
